import { MasterUseCaseInterface } from 'application/usecases/MasterUseCase';
import { PropertyUseCaseInterface } from 'application/usecases/PropertyUseCase';
import { UserUseCaseInterface } from 'application/usecases/UserUseCase';
import { ListNoData } from 'presentation/components/ListNoData';
import { openErrorNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch, useAppSelector } from 'presentation/redux/hooks';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_PARAMS, PARAMS } from 'utilities/constants/PARAMETER';
import { ListProperty } from './components/List';
import { SearchDrawer } from './components/Search';
import { Pagination } from 'antd';
import { customItemRender } from './components/CustomItemRender';
import { MasterAreaData } from './models/Master';
import { PropertyData } from './models/Property';

export const LOCAL_PARAMS = {
    BUYNOWPRICEMAX: 'buyNowPriceMax',
    OLDMAX: 'oldMax',
    OLDMIN: 'oldMin',
    AREAID: 'areaId',
    BUYNOWPRICEMIN: 'buyNowPriceMin',
    ISBALANCED: 'isBalanced',
};

interface props {
    propertyUseCase: PropertyUseCaseInterface;
    masterUseCase: MasterUseCaseInterface;
    userUseCase: UserUseCaseInterface;
}

const CU00201Page = ({ propertyUseCase, masterUseCase, userUseCase }: props) => {
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const keyword = searchParams.get(PARAMS.KEYWORD) || DEFAULT_PARAMS.KEYWORD;
    const buyNowPriceMin = searchParams.get(LOCAL_PARAMS.BUYNOWPRICEMIN) || '0';
    const buyNowPriceMax = searchParams.get(LOCAL_PARAMS.BUYNOWPRICEMAX) || '4000';
    const oldMin = searchParams.get(LOCAL_PARAMS.OLDMIN) || '0';
    const oldMax = searchParams.get(LOCAL_PARAMS.OLDMAX) || '40';
    const areaId = searchParams.get(LOCAL_PARAMS.AREAID);
    const isBalanced = searchParams.get(LOCAL_PARAMS.ISBALANCED);

    const [listProperty, setListProperty] = useState<PropertyData[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PARAMS.PAGE);
    const [total, setTotal] = useState<number>(0);
    const [masterArea, setMasterArea] = useState<MasterAreaData[]>();

    const fetchingCount = useAppSelector((state) => state.app.fetchingCount);
    const [isRefresh, setIsRefresh] = useState<boolean>(false);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const shouldScrollAfterFetchRef = useRef<boolean>(false);

    const userSlice = useAppSelector((state) => state.user);

    useEffect(() => {
        (async () => {
            try {
                dispatch(startFetching());
                if (!masterArea) return;
                const areaIds = masterArea.map((element) => element.code);

                const response = await propertyUseCase.getListedPropertiesList({
                    page: currentPage,
                    pageSize: DEFAULT_PARAMS.SIZE,
                    keyword,
                    buyNowPriceMin: Number(buyNowPriceMin) * 10000,
                    buyNowPriceMax: Number(buyNowPriceMax) * 10000,
                    oldMin: Number(oldMin),
                    oldMax: Number(oldMax),
                    filter: isBalanced === 'true' ? 1 : 0,
                    areaId:
                        areaId !== null ? (areaId === '' ? undefined : areaId) : areaIds.toString(),
                    isLogin: userSlice?.isSignedIn ? 1 : 0,
                });

                setTotal(response.result?.pagination?.count ?? 0);
                setListProperty(response.result?.data ?? []);
            } catch (error: any) {
                openErrorNoti();
            } finally {
                dispatch(completeFetching());
            }
        })();
    }, [currentPage, isRefresh, masterArea]);

    useEffect(() => {
        if (fetchingCount !== 0 || !shouldScrollAfterFetchRef.current) return;

        const scrollToTop = () => {
            scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        };

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                scrollToTop();
                setTimeout(scrollToTop, 0);
                shouldScrollAfterFetchRef.current = false;
            });
        });
    }, [fetchingCount]);

    useEffect(() => {
        setTotal(1);
        setCurrentPage(1);
        setIsRefresh((prev) => !prev);
    }, [keyword, buyNowPriceMin, buyNowPriceMax, oldMin, oldMax, areaId, isBalanced]);

    useEffect(() => {
        // if(!userSlice?.isSignedIn) return;
        (async () => {
            try {
                dispatch(startFetching());
                const areaData = await masterUseCase.getMasterAreasSearchAreasForSearch();

                setMasterArea(areaData ?? []);
            } catch (error: any) {
                openErrorNoti();
            } finally {
                dispatch(completeFetching());
            }
        })();
    }, []);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexFlow: 'column',
                textAlign: 'center',
            }}
            className="CU-002-01-container "
        >
            <section
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingRight: 40,
                }}
            >
                <PageTitle title="売り物件一覧" />
                <SearchDrawer data={masterArea ?? []} userUseCase={userUseCase} />
            </section>

            <section
                ref={scrollContainerRef}
                style={{
                    height: '100%',
                    overflow: 'auto',
                    flexGrow: 1,
                    visibility: fetchingCount === 0 ? 'visible' : 'hidden',
                }}
            >
                {listProperty && listProperty.length > 0 ? (
                    <ListProperty data={listProperty} propertyUseCase={propertyUseCase} />
                ) : (
                    <div style={{ paddingRight: 40 }}>
                        <ListNoData />
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                    <Pagination
                        current={currentPage}
                        pageSize={DEFAULT_PARAMS.SIZE}
                        total={total}
                        showLessItems
                        onChange={(page) => {
                            shouldScrollAfterFetchRef.current = true;
                            setCurrentPage(page);
                        }}
                        itemRender={customItemRender}
                        style={{ margin: '20px 0', textAlign: 'center' }}
                    />
                </div>
            </section>
        </div>
    );
};

export default CU00201Page;
